import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { getSupportedDetailedTypes } from 'lib/analytics';
import { Button } from 'components/common';
import {
  useFiltersContext,
  useFiltersDispatch
} from 'components/common/filter';

import type { ButtonCategory } from 'components/common/Button';
import type { IconProp } from 'components/common/icons/Icon';

const AnalyticsPageFiltersContentTypes = () => {
  const filterDispatch = useFiltersDispatch();

  const filterState = useFiltersContext();

  const { messages, theme } = useReduxState();

  const router = useRouter();

  const isDetailedPage = router.pathname.includes('/analytics/detailed');

  const handleContentTypeChange = (type: keyof typeof contentTypes) => {
    // preserve the generic query params, but clear the content type
    const query = { ...router.query };
    Object.values(contentTypes).forEach(({ id }) => {
      delete query[id];
    });
    delete query.achievementTypes; // additional filter in achievements
    delete query.competenceProfiles; // additional filter in competencies

    // clear the filter state for the content type we are moving from
    const currentType = query.type as keyof typeof contentTypes;
    if (currentType) {
      const state = { ...filterState?.state?.[currentType] };
      delete state[contentTypes[currentType].id];
      filterDispatch({ filterId: currentType, state, type: 'updateState' });
    }

    query.type = type;
    router.replace({ query }, undefined, { shallow: true });
  };

  const contentTypes = getSupportedDetailedTypes({ messages });

  return isDetailedPage ? (
    <div className="content-types">
      {Object.keys(contentTypes).map((contentType) => {
        const type = contentType as keyof typeof contentTypes;

        const isActive = router.query.type === type;

        return (
          <Button
            key={type}
            category={'transparent' as ButtonCategory}
            customBackgroundColor={isActive ? theme.lightGrey : 'transparent'}
            customBackgroundColorHover={theme.lightGrey}
            icon={contentTypes[type].icon as IconProp}
            onClick={() => handleContentTypeChange(type)}
            size="header"
            text={contentTypes[type].labels.buttonText}
          />
        );
      })}

      <style jsx>{`
        .content-types {
          border-bottom: 1px solid ${theme.lightGrey};
          display: flex;
          flex-direction: row;
          gap: 10px;
          padding: 10px;
          overflow-x: scroll;
        }
      `}</style>
    </div>
  ) : null;
};

export default AnalyticsPageFiltersContentTypes;
